"use client";

import { useEffect, useState } from "react";
import { useParams } from "next/navigation";

import { Rosette } from "@/features/home-v2/ornament";
import "@/features/home-v2/states/route-loading.css";

// Route-scoped loading.tsx fallback, shared by segments where the entity can
// never be missing (lists, dashboard, account pages). Deliberately NOT used
// as a root-level app/loading.tsx: a loading.js file wraps every nested
// segment (including dynamic detail routes) in a Suspense boundary, which
// commits the initial HTML response with a 200 status before an async
// notFound() call further down can run — turning every not-found detail
// page into a fake-200 soft 404. Keep this out of any segment whose
// children (or which itself) can call notFound().
//
// This rationale is now the design system's, not this component's: the full
// loading-boundary policy — including the separate, equally silent failure
// where a route-level loading.tsx emitting real server markup leaves the
// segment un-hydrated (which is why /portfolio has none, and must keep
// having none) — lives in
// design-system/04-engineering-rules.md §6, "Loading boundaries".
// It is duplicated here on purpose: the trap is invisible at the call site,
// so the warning has to be where someone reaches for this component.
export default function RouteLoadingSpinner() {
  const [mounted, setMounted] = useState(false);
  const params = useParams<{ lang?: string }>();

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  // In v2 the shell stays put while a route loads, so the loader sits in the
  // content area only — the ده‌پر rosette turning slowly round a gilt diamond —
  // instead of a full-viewport blur over the chrome (and no Lottie download).
  return (
    <div className="v2-route-loading" role="status">
      <span className="v2-route-loading-mark" aria-hidden>
        <Rosette className="v2-route-loading-rosette" />
        <i />
      </span>
      <span className="v2-sr-only">{params?.lang === "fa" ? "در حال بارگذاری" : "Loading"}</span>
    </div>
  );
}
